import React, { useState } from "react";
import axios from "axios";
import IssueList from "./IssueList";
import Papa from "papaparse";
import IssueChart from "./IssueChart";
import DataTable from "./DataTable";
import ColumnProfile from "./ColumnProfile";
import AISuggestionsPanel from "./AISuggestionsPanel";

const API_BASE = "https://subhankar7-datacleanerapi.hf.space";

export default function UploadCSV() {
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState([]);
  const [fullData, setFullData] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFixes, setSelectedFixes] = useState({});
  const [fixesApplied, setFixesApplied] = useState(false);

  // ---------------------- FILE UPLOAD ----------------------
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);

    // RESET UI
    setIssues([]);
    setPreview([]);
    setFullData([]);
    setSelectedFixes({});
    setFixesApplied(false);   // <<< IMPORTANT FIX

    setFileName(file.name);

    const fd = new FormData();
    fd.append("file", file);

    try {
      const res = await axios.post(`${API_BASE}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setPreview(res.data.preview);
      setFullData(res.data.data);
      setIssues(res.data.issues);
    } catch (err) {
      console.error(err);
      alert("Failed to upload file");
    }

    setLoading(false);
  };

  // ---------------------- ACCEPT FIXES (SELECTION) ----------------------
  const toggleAccept = (issue) => {
    const key = `${issue.row}-${issue.col}`;

    if (selectedFixes[key]) {
      const copy = { ...selectedFixes };
      delete copy[key];
      setSelectedFixes(copy);
    } else {
      setSelectedFixes({
        ...selectedFixes,
        [key]: {
          row: issue.row,
          col: issue.col,
          value: issue.suggestion || issue.original,
        },
      });
    }
  };

  // ---------------------- APPLY SELECTED FIXES ----------------------
  const applyFixes = async () => {
    const fixes = Object.values(selectedFixes);

    if (fixes.length === 0) {
      alert("No fixes selected.");
      return;
    }

    try {
      const res = await axios.post(`${API_BASE}/apply`, {
        data: fullData,
        fixes: fixes,
      });

      const cleaned = res.data.cleaned;

      setFullData(cleaned);
      setPreview(cleaned.slice(0, 10));
      setIssues([]);
      setSelectedFixes({});
      setFixesApplied(true);  // <<< SHOW DOWNLOAD BUTTON

      alert("Fixes applied successfully! Now you can download cleaned CSV.");
    } catch (err) {
      console.error(err);
      alert("Failed to apply fixes");
    }
  };

  // ---------------------- APPLY SINGLE AI FIX ----------------------
  const applySingleFix = (issue) => {
    const updated = [...fullData];
    updated[issue.row][issue.col] = issue.suggestion;

    setFullData(updated);
    setPreview(updated.slice(0, 10));

    alert(`AI suggestion applied for ${issue.col} (row ${issue.row})`);
  };

  // ---------------------- DOWNLOAD CLEANED CSV ----------------------
  const downloadCSV = () => {
    if (!fullData.length) {
      alert("No cleaned data to download.");
      return;
    }

    const csv = Papa.unparse(fullData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `cleaned_${fileName}`;
    link.click();
  };

  // ---------------------- UI ----------------------
  return (
    <div
      style={{
        maxWidth: "1100px",
        padding: "20px",
        fontFamily: "Inter, sans-serif",
        color: "#333",
        marginLeft: "40px",
      }}
    >
      {/* TOP CARD */}
      <div
        style={{
          background: "#ffffff",
          padding: "20px",
          borderRadius: "10px",
          boxShadow: "0px 3px 12px rgba(0,0,0,0.08)",
          border: "1px solid #eee",
          marginBottom: "20px",
        }}
      >
        <h2 style={{ marginBottom: 10 }}>📤 Upload CSV File</h2>
        <p style={{ color: "#555" }}>The system will automatically detect:</p>

        <ul style={{ lineHeight: "1.8", marginLeft: 20, color: "#444" }}>
          <li>✔ Missing values</li>
          <li>✔ Outliers (IQR)</li>
          <li>✔ Invalid formats (email, phone, date)</li>
          <li>✔ Category inconsistencies</li>
          <li>✔ Duplicate rows</li>
        </ul>

        <input
          type="file"
          accept=".csv"
          onChange={handleUpload}
          style={{
            marginTop: "15px",
            padding: "8px",
            borderRadius: "6px",
            border: "1px solid #ccc",
            background: "#f8f8f8",
          }}
        />
      </div>

      {/* PREVIEW TABLE */}
      <h3 style={{ marginTop: 30, marginBottom: 10 }}>📋 Preview (first 10 rows)</h3>
      <div
        style={{
          background: "#fff",
          padding: "15px",
          borderRadius: "10px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          marginBottom: "20px",
        }}
      >
        <DataTable data={preview} issues={issues} />
      </div>

      {/* COLUMN STATS + CHART */}
      <ColumnProfile data={fullData} />
      <IssueChart issues={issues} />

      {/* ISSUE LIST */}
      <h3 style={{ marginTop: 30 }}>🚨 Issues Found ({issues.length})</h3>

      {loading && (
        <p style={{ color: "blue", fontStyle: "italic" }}>
          Analyzing CSV... Please wait ⏳ (1–2 minutes)
        </p>
      )}

      <div
        style={{
          background: "#fff",
          padding: "15px",
          borderRadius: "10px",
          boxShadow: "0px 3px 12px rgba(0,0,0,0.05)",
          marginTop: 10,
          marginBottom: 20,
        }}
      >
        <IssueList
          issues={issues}
          selectedFixes={selectedFixes}
          toggleAccept={toggleAccept}
          loading={loading}
        />
      </div>

      {/* AI SUGGESTIONS PANEL */}
      <AISuggestionsPanel issues={issues} applySingleFix={applySingleFix} />

      {/* ACTION BUTTONS */}
      <div style={{ marginTop: 20 }}>
        <button
          onClick={applyFixes}
          style={{
            background: "#4CAF50",
            color: "white",
            padding: "10px 18px",
            borderRadius: "6px",
            border: "none",
            cursor: "pointer",
            fontSize: "15px",
            marginRight: 10,
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          }}
        >
          Apply Selected Fixes
        </button>

        {fixesApplied ? (
          <button
            onClick={downloadCSV}
            style={{
              background: "#1976D2",
              color: "white",
              padding: "10px 18px",
              borderRadius: "6px",
              border: "none",
              cursor: "pointer",
              fontSize: "15px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            }}
          >
            Download Cleaned CSV
          </button>
        ) : (
          <p style={{ color: "gray", marginTop: 10 }}>
            Apply fixes to enable download.
          </p>
        )}
      </div>
    </div>
  );
}
